/**
 * WishlistPreview - Shows wishlist items in profile page
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Heart, ShoppingBag, Trash2, Loader2 } from "lucide-react";
import { formatBHD } from "@/lib/utils";
import { toast } from "sonner";

interface Product {
  id: string;
  name: string;
  price: number;
  sale_price?: number;
  images: Array<{ url: string }>;
}

interface WishlistItem {
  id: string;
  product_id: string;
  product: Product;
}

interface WishlistPreviewProps {
  userId: string;
  onRemove?: () => void;
}

export function WishlistPreview({ userId, onRemove }: WishlistPreviewProps) {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchWishlistItems();
  }, [userId]);

  const fetchWishlistItems = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('wishlists')
        .select(`
          id,
          product_id,
          products (
            id,
            name,
            price,
            sale_price,
            product_images (
              url,
              position
            )
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      const transformedItems: WishlistItem[] = (data || [])
        .filter(item => item.products)
        .map(item => ({
          id: item.id,
          product_id: item.product_id,
          product: {
            id: (item.products as any).id,
            name: (item.products as any).name,
            price: (item.products as any).price,
            sale_price: (item.products as any).sale_price,
            images: ((item.products as any).product_images || [])
              .sort((a: any, b: any) => a.position - b.position)
              .map((img: any) => ({ url: img.url })),
          },
        }));

      setItems(transformedItems);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      toast.error("Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (wishlistId: string) => {
    try {
      const { error } = await supabase
        .from('wishlists')
        .delete()
        .eq('id', wishlistId);

      if (error) throw error;

      setItems(items.filter(item => item.id !== wishlistId));
      toast.success("Removed from wishlist");
      onRemove?.();
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      toast.error("Failed to remove from wishlist");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-sm text-muted-foreground mt-2">Loading wishlist...</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900 mb-4">
          <Heart className="h-8 w-8 text-red-600 dark:text-red-200" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Your wishlist is empty</h3>
        <p className="text-muted-foreground mb-4">
          Start adding products you love!
        </p>
        <Button onClick={() => navigate("/shop")}>
          <ShoppingBag className="mr-2 h-4 w-4" />
          Browse Products
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {items.map((item) => (
        <div
          key={item.id}
          className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => navigate(`/product/${item.product.id}`)}
        >
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-muted rounded-md overflow-hidden flex-shrink-0">
              {item.product.images[0] ? (
                <img
                  src={item.product.images[0].url}
                  alt={item.product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingBag className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium line-clamp-1">{item.product.name}</p>
              <div className="flex items-center gap-2 mt-1">
                {item.product.sale_price && item.product.sale_price < item.product.price ? (
                  <>
                    <p className="text-lg font-bold text-primary">
                      {formatBHD(item.product.sale_price)}
                    </p>
                    <p className="text-sm text-muted-foreground line-through">
                      {formatBHD(item.product.price)}
                    </p>
                  </>
                ) : (
                  <p className="text-lg font-bold">{formatBHD(item.product.price)}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(item.id);
              }}
              className="hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
      <div className="text-center pt-4">
        <Button variant="outline" onClick={() => navigate("/wishlist")}>
          View All Wishlist Items
        </Button>
      </div>
    </div>
  );
}
